import { useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ROLE_LABELS } from "@easyshop/shared";
import { Avatar, Dropdown, cx } from "@/components/ui";
import { useSession } from "@/hooks/useSession";
import { useConversations, useTodayDashboard } from "@/lib/queries";
import { eur, initials } from "@/lib/format";
import { Freshness, NotificationsMenu, logout } from "./HeaderControls";
import { NetworkSwitcher } from "./NetworkSwitcher";

interface NavItem {
  to: string;
  label: string;
  badge?: number;
  show?: boolean;
  /** Actif selon l'adresse complète (onglets d'une boutique : ?tab=…). */
  active?: (path: string, search: string) => boolean;
}

/** Grand titre par défaut des pages qui n'ont pas leur propre en-tête. */
const TITLES: [string, string][] = [
  ["/boutiques", "Boutiques"],
  ["/arrivages", "Arrivages"],
  ["/messages", "Messages & tâches"],
  ["/promos", "Promos & campagnes"],
  ["/planning", "Planning des équipes"],
  ["/acces", "Accès & équipe"],
  ["/parametres", "Paramètres du réseau"],
  ["/it", "IT"],
  ["/profil", "Profil"],
  ["/reseaux", "Tous les réseaux"],
  ["/administration", "Administration plateforme"],
];

const rowClass = (active: boolean) =>
  cx(
    "flex min-h-[34px] items-center gap-2 rounded-[8px] px-[10px] text-[14px]",
    active ? "bg-[oklch(0.4_0.08_155/0.12)] font-semibold text-accent" : "text-ink hover:bg-[oklch(0.2_0.01_150/0.05)]"
  );

function SideLink({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  const loc = useLocation();
  const custom = item.active?.(loc.pathname, loc.search);
  return (
    <NavLink to={item.to} end={item.to === "/"} onClick={onNavigate} className={({ isActive }) => rowClass(custom ?? isActive)}>
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.badge ? <span className="rounded-full bg-ios-badge px-[6px] text-[11px] font-semibold leading-[17px] text-white">{item.badge}</span> : null}
    </NavLink>
  );
}

/** Section de la barre latérale ; repliable si `open` / `onToggle` sont fournis. */
function SideSection({ title, children, open, onToggle, count }: { title: string; children: ReactNode; open?: boolean; onToggle?: () => void; count?: number }) {
  return (
    <div className="mt-5">
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex w-full items-center gap-[6px] rounded-[6px] px-[10px] pb-[6px] text-left text-[12px] font-medium text-muted hover:text-ink"
        >
          <span className="flex-1">{title}</span>
          {!open && count != null && <span className="tabular">{count}</span>}
          <span aria-hidden className={cx("text-[10px] transition-transform", open ? "rotate-90" : "")}>
            ›
          </span>
        </button>
      ) : (
        <div className="px-[10px] pb-[6px] text-[12px] font-medium text-muted">{title}</div>
      )}
      <div className="flex flex-col gap-[2px]">{children}</div>
    </div>
  );
}

const BOUTIQUES_OPEN_KEY = "pilote.sidebar.boutiques";

/** Section « Boutiques » ouverte ou repliée, retenue sur cet appareil. */
function useSectionOpen(key: string): [boolean, () => void] {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(key) !== "0";
    } catch {
      return true;
    }
  });
  const toggle = () =>
    setOpen((o) => {
      try {
        localStorage.setItem(key, o ? "0" : "1");
      } catch {
        // Stockage indisponible (navigation privée) : l'état reste en mémoire.
      }
      return !o;
    });
  return [open, toggle];
}

/** Personne connectée (bas de la barre latérale) : profil, réseau, déconnexion. */
function UserBlock() {
  const { me, can } = useSession();
  const navigate = useNavigate();
  const role = me.organizations.find((o) => o.id === me.organization?.id)?.viaPlatform ? "Administrateur plateforme" : me.role ? ROLE_LABELS[me.role] : "";
  const item = "block w-full rounded-lg px-3 py-[9px] text-left text-[13px] font-medium text-ink-2 hover:bg-ios-fill";
  return (
    <Dropdown
      width={220}
      align="left"
      placement="top"
      trigger={(_, toggle) => (
        <button type="button" onClick={toggle} className="flex w-full items-center gap-[10px] rounded-[8px] px-[6px] py-[6px] text-left hover:bg-[oklch(0.2_0.01_150/0.05)]">
          <Avatar text={initials(me.user.name)} className="!h-8 !w-8 !text-[12px]" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-semibold">{me.user.name}</span>
            <span className="block truncate text-[11.5px] text-muted">{role}</span>
          </span>
        </button>
      )}
    >
      {(close) => (
        <>
          <div className="px-3 pb-2 pt-1">
            <div className="text-[13px] font-semibold">{me.user.name}</div>
            <div className="text-[11.5px] text-muted">{me.user.email}</div>
          </div>
          <button type="button" className={item} onClick={() => (close(), navigate("/profil"))}>
            Profil
          </button>
          {me.organizations.length > 1 && (
            <button type="button" className={item} onClick={() => (close(), navigate("/profil#reseaux"))}>
              Changer de réseau
            </button>
          )}
          {can("boutiques.manage") && (
            <button type="button" className={item} onClick={() => (close(), navigate("/parametres"))}>
              Paramètres du réseau
            </button>
          )}
          <div className="my-1 h-px bg-ios-sep" />
          <button type="button" className={cx(item, "text-ios-badge")} onClick={() => void logout()}>
            Se déconnecter
          </button>
        </>
      )}
    </Dropdown>
  );
}

/**
 * Shell PC « style iOS » : barre latérale blanche 236 px (réseau, navigation,
 * boutiques avec leur CA du jour — section repliable —, outils, personne connectée) ; chaque page
 * affiche son grand titre (PageHeader).
 */
export function DesktopShell() {
  const { me, can, isManagerView } = useSession();
  const conv = useConversations("tous");
  const unread = (conv.data ?? []).reduce((s, c) => s + c.unread, 0);
  const today = useTodayDashboard(can("dashboard.read"));
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [boutiquesOpen, toggleBoutiques] = useSectionOpen(BOUTIQUES_OPEN_KEY);
  const close = () => setMenuOpen(false);
  // Gérant d'une seule boutique : l'accueil est l'aperçu de sa boutique.
  const single = !me.networkWide && me.boutiques.length === 1 ? me.boutiques[0] : null;
  const tabOf = (search: string) => new URLSearchParams(search).get("tab");

  const main: NavItem[] = single
    ? [
        { to: "/", label: "Accueil", active: (p, s) => p === "/" && !tabOf(s) },
        { to: `/?tab=stock`, label: "Stock", show: can("stock.read"), active: (p, s) => p === "/" && tabOf(s) === "stock" },
        { to: `/?tab=arrivages`, label: "Arrivages", show: can("arrivals.read"), active: (p, s) => p === "/" && tabOf(s) === "arrivages" },
      ]
    : [
        { to: "/", label: "Accueil", show: can("dashboard.read") },
        { to: "/produits", label: "Produits", show: can("stock.read"), active: (p) => p.startsWith("/produits") },
      ];
  const tools: NavItem[] = [
    { to: "/messages", label: "Messages & tâches", badge: unread, show: can("messages.read") },
    { to: "/promos", label: "Promos & campagnes", show: can("campaigns.read") },
    { to: "/planning", label: "Planning", show: can("planning.read") },
    { to: "/arrivages", label: "Arrivages", show: !single && can("arrivals.read") },
    { to: "/acces", label: "Accès & équipe", show: can("members.read") },
    { to: "/parametres", label: "Paramètres", show: can("boutiques.manage") },
  ];
  const platform: NavItem[] = [
    { to: "/reseaux", label: "Tous les réseaux", badge: me.pendingApprovals, show: me.platformAdmin },
    { to: "/administration", label: "Administration", show: me.platformAdmin },
  ];
  const visible = (l: NavItem[]) => l.filter((n) => n.show !== false);
  // Page IT : seulement si le réseau l'a activée (Paramètres du réseau) et pour qui en a le droit.
  const showIt = Boolean(me.organization?.itEnabled) && can("it.manage");
  const caToday = new Map((today.data?.boutiques ?? []).map((b) => [b.id, b]));
  const villes = me.boutiques.map((b) => b.city);
  const subtitle = isManagerView ? `Mes boutiques · ${villes.join(", ")}` : `Réseau · ${me.networkSize} boutiques`;
  // Pages sans grand titre à elles : titre par défaut (les autres affichent leur PageHeader).
  const path = loc.pathname.replace(/\/+$/, "") || "/";
  const ownHeader = path === "/" || path.startsWith("/boutiques/") || path.startsWith("/produits");
  const defaultTitle = TITLES.find(([p]) => path.startsWith(p))?.[1];

  const sidebar = (
    <div className="flex h-full w-[236px] flex-shrink-0 flex-col overflow-y-auto border-r-[0.5px] border-ios-sep bg-white px-3 pb-3 pt-5">
      <div className="mb-5 flex items-center gap-1 px-[6px]">
        <NetworkSwitcher subtitle={subtitle} />
        <NotificationsMenu align="left" />
      </div>
      <div className="flex flex-col gap-[2px]">
        {visible(main).map((n) => (
          <SideLink key={n.to} item={n} onNavigate={close} />
        ))}
      </div>

      {!single && can("dashboard.read") && me.boutiques.length > 0 && (
        <SideSection title={me.networkWide ? "Boutiques" : "Mes boutiques"} open={boutiquesOpen} onToggle={toggleBoutiques} count={me.boutiques.length}>
          {/* Repliée : seule la boutique ouverte reste visible. */}
          {me.boutiques.filter((b) => boutiquesOpen || loc.pathname.startsWith(`/boutiques/${b.id}`)).map((b) => {
            const card = caToday.get(b.id);
            return (
              <NavLink key={b.id} to={`/boutiques/${b.id}`} onClick={close} className={({ isActive }) => rowClass(isActive)}>
                <span className={cx("h-2 w-2 flex-shrink-0 rounded-full", (card?.type ?? b.type) === "FRANCHISE" ? "bg-franchise" : "bg-positive")} />
                <span className="min-w-0 flex-1 truncate">{b.city}</span>
                {card && <span className="flex-shrink-0 text-[12px] font-normal tabular text-muted">{eur(card.ca)}</span>}
              </NavLink>
            );
          })}
        </SideSection>
      )}

      {visible(tools).length > 0 && (
        <SideSection title="Outils">
          {visible(tools).map((n) => (
            <SideLink key={n.to} item={n} onNavigate={close} />
          ))}
        </SideSection>
      )}
      {visible(platform).length > 0 && (
        <SideSection title="Plateforme">
          {visible(platform).map((n) => (
            <SideLink key={n.to} item={n} onNavigate={close} />
          ))}
        </SideSection>
      )}

      <div className="flex-1" />
      {showIt && (
        <div className="mt-4 flex flex-col gap-[2px]">
          <SideLink item={{ to: "/it", label: "IT · suivi des livraisons" }} onNavigate={close} />
        </div>
      )}
      <div className="mt-4 flex flex-col gap-2 border-t-[0.5px] border-ios-sep pt-3">
        <Freshness />
        <UserBlock />
      </div>
    </div>
  );

  return (
    <div className="ios flex min-h-[100dvh] bg-ios-bg text-ink">
      <div className="sticky top-0 hidden h-[100dvh] lg:block">{sidebar}</div>
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-[rgba(20,30,20,0.35)] lg:hidden" onClick={close} />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">{sidebar}</div>
        </>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="sticky top-0 z-20 flex items-center gap-3 border-b-[0.5px] border-ios-sep bg-ios-bar px-4 py-2 pt-[calc(8px+env(safe-area-inset-top))] backdrop-blur-xl lg:hidden">
          <button type="button" aria-label="Menu" onClick={() => setMenuOpen(true)} className="flex h-9 w-9 items-center justify-center text-[20px] text-accent">
            ☰
          </button>
          <span className="truncate text-[15px] font-semibold">{me.organization?.name ?? "Pilote"}</span>
        </div>
        <main className="min-w-0 flex-1 px-4 py-6 md:px-8 md:py-7">
          {!ownHeader && defaultTitle ? (
            <PageStack>
              <PageHeader title={defaultTitle} />
              <Outlet />
            </PageStack>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}

/** Grand titre de page (30 px) + sous-titre, et contenu à droite (onglets…). */
export function PageHeader({ title, subtitle, badge, right, back }: { title: ReactNode; subtitle?: ReactNode; badge?: ReactNode; right?: ReactNode; back?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="min-w-0 flex-1">
        {back}
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="truncate text-[30px] font-bold leading-[36px] tracking-[0.2px]">{title}</h1>
          {badge}
        </div>
        {subtitle && <div className="mt-[2px] truncate text-[15px] text-muted">{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}

export function PageStack({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("flex flex-col gap-[22px]", className)}>{children}</div>;
}
